import apiClient from './apiClient'
import authStore from '@/store/authStore'
import Cookies from 'js-cookie'

export const login = async (usernameOrEmail, password) => {
  try {
    const response = await apiClient.post('/auth/login/', { username_or_email: usernameOrEmail, password })
    const { user, access, refresh, is_staff } = response.data
    
    // Guardar datos en localStorage
    localStorage.setItem('accessToken', access)
    localStorage.setItem('refreshToken', refresh)
    localStorage.setItem('isAdmin', is_staff)
    localStorage.setItem('userId', user.id)
    localStorage.setItem('username', user.username || user.email)
    
    // Invalidar caché de productos para forzar recarga con precios
    localStorage.removeItem('products')
    localStorage.removeItem('productsTimestamp')
    
    return {
      user,
      is_admin: is_staff
    }
  } catch (error) {
    console.error('Error al iniciar sesión:', error)
    
    // Preservar la respuesta completa del error para casos especiales
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Si es un error especial (cuenta no verificada, etc.), preservar la estructura
      if (errorData.error && typeof errorData.error === 'string') {
        const customError = new Error(errorData.message || 'Error al iniciar sesión');
        customError.response = error.response;
        throw customError;
      }
    }
    
    throw new Error(
      error.response?.data?.error || 
      'Error al iniciar sesión. Por favor, verifica tus credenciales.'
    )
  }
}

export const register = async (userData) => {
  try {
    const response = await apiClient.post('/auth/register/', userData);
    
    if (response.data) {
      return {
        message: response.data.message,
        email: response.data.email,
        userId: response.data.user_id,
        show_verification: response.data.show_verification,
        email_delivery_issue: response.data.email_delivery_issue,
      };
    }
    
    throw new Error(response.data?.message || 'Error en el registro');
  } catch (error) {
    // Preservar la respuesta completa del error para casos especiales
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Si es un error especial (cuenta existente, etc.), preservar la estructura
      if (errorData.error && typeof errorData.error === 'string') {
        const customError = new Error(errorData.message || 'Error en el registro');
        customError.response = error.response;
        throw customError;
      }
      
      // Si es un array de errores de validación
      if (errorData.error && Array.isArray(errorData.error)) {
        const customError = new Error('Errores de validación');
        customError.errors = errorData.error;
        throw customError;
      }
    }
    throw error;
  }
};

export const logout = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('isAdmin');
  localStorage.removeItem('userId');
  localStorage.removeItem('username');
  localStorage.removeItem('intendedRoute');
  localStorage.removeItem('userPermissions');
  localStorage.removeItem('userPermissionsTimestamp');
  
  // Invalidar caché de productos para forzar recarga sin precios
  localStorage.removeItem('products');
  localStorage.removeItem('productsTimestamp');
  
  // Invalidar caché de órdenes para forzar refresh al volver a entrar
  localStorage.removeItem('userOrders');
  localStorage.removeItem('adminOrders');
  localStorage.removeItem('ordersTimestamp');
  
  // Limpiar estado de bypass de mantenimiento directamente
  localStorage.removeItem('maintenance_bypass');
  localStorage.removeItem('maintenance_bypass_expiry');
  localStorage.removeItem('maintenance_bypass_timestamp');
  
  // Limpiar cookie del carrito
  Cookies.remove('cart', { path: '/' });
  
  // Actualizar el estado de autenticación en el store
  authStore.clearUser();
};

export const isAuthenticated = () => {
  return localStorage.getItem('accessToken') !== null;
};

export const isAdmin = () => {
  return localStorage.getItem('isAdmin') === 'true';
};

export const getUsername = () => {
  return localStorage.getItem('username')
}

export const getAccessToken = () => {
  return localStorage.getItem('accessToken')
}

export const getUserInfo = async () => {
  try {
    const token = getAccessToken()
    const response = await apiClient.get('/auth/profile/', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al obtener información del usuario')
  }
}

export const verifyPermissions = async () => {
  try {
    const token = getAccessToken()
    if (!token) {
      throw new Error('No hay token de acceso')
    }

    // Verificar si tenemos permisos en caché (válidos por 5 minutos)
    const cachedPermissions = localStorage.getItem('userPermissions')
    const permissionsTimestamp = localStorage.getItem('userPermissionsTimestamp')
    const now = Date.now()
    
    if (cachedPermissions && permissionsTimestamp) {
      const cacheAge = now - parseInt(permissionsTimestamp)
      if (cacheAge < 5 * 60 * 1000) { // 5 minutos
        return JSON.parse(cachedPermissions)
      }
    }

    // Si no hay caché válido, consultar al backend
    const response = await apiClient.get('/auth/verify-permissions/', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    
    const permissions = response.data
    
    // Guardar en caché
    localStorage.setItem('userPermissions', JSON.stringify(permissions))
    localStorage.setItem('userPermissionsTimestamp', now.toString())
    
    // Actualizar isAdmin en localStorage con el valor del backend
    localStorage.setItem('isAdmin', permissions.is_staff.toString())
    
    return permissions
  } catch (error) {
    console.error('Error verificando permisos:', error)
    // Si hay error, limpiar caché y redirigir a login
    localStorage.removeItem('userPermissions')
    localStorage.removeItem('userPermissionsTimestamp')
    localStorage.removeItem('isAdmin')
    throw error
  }
}

export const hasAdminPermission = async () => {
  try {
    const permissions = await verifyPermissions()
    return permissions.permissions.can_access_admin
  } catch (error) {
    return false
  }
}

export const checkPermission = async (permission) => {
  try {
    const permissions = await verifyPermissions()
    return permissions.permissions[permission] || false
  } catch (error) {
    return false
  }
}

export const updateUserProfile = async (userData) => {
  try {
    const response = await apiClient.put('/auth/profile/', userData)
    return response.data
  } catch (error) {
    throw error.response?.data || error
  }
}

export const changePassword = async (currentPassword, newPassword) => {
  try {
    const token = getAccessToken()
    const response = await apiClient.post('/auth/change-password/', {
      old_password: currentPassword,
      new_password: newPassword
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    return response.data
  } catch (error) {
    const message = error.response?.data?.message || 'Error al cambiar la contraseña'
    throw new Error(message)
  }
}

export const verifyEmail = async (data) => {
  const response = await apiClient.post('/auth/verify-email/', {
    email: data.email,
    code: data.code
  });
  return response.data;
};

export const resendVerification = async (email) => {
  const response = await apiClient.post('/auth/resend-verification/', {
    email
  });
  return response.data;
};

export const requestVerificationCode = async (type, newEmail = null, password = null) => {
  try {
    const response = await apiClient.post('/auth/verify-change/', {
      type,
      new_email: newEmail,
      password: password
    })
    return response.data
  } catch (error) {
    throw error.response?.data || error
  }
}

export const updateEmailWithVerification = async (email, verificationCode) => {
  try {
    const response = await apiClient.put('/auth/profile/', {
      email,
      verification_code: verificationCode
    })
    return response.data
  } catch (error) {
    throw error.response?.data || error
  }
}

export const requestPasswordReset = async (email) => {
  try {
    console.log('Requesting password reset for email:', email);
    const response = await apiClient.post('/auth/request-password-reset/', { email }, {
      timeout: 60000 // 60 seconds timeout specifically for this request
    });
    return response.data;
  } catch (error) {
    console.error('Password reset request error:', error);
    
    // Special handling for timeout or network errors
    if (!error.response) {
      throw new Error('Error de conexión. Puede que el código se haya generado, intenta con el paso de verificación.');
    }
    
    if (error.response?.data) {
      throw error;
    }
    throw new Error('Error al solicitar el restablecimiento de contraseña');
  }
};

export const verifyPasswordReset = async (data) => {
  try {
    console.log('Verifying password reset code for email:', data.email);
    const response = await apiClient.post('/auth/verify-password-reset/', {
      email: data.email,
      code: data.code,
      new_password: data.new_password
    }, {
      timeout: 45000 // 45 seconds timeout specifically for this request
    });
    return response.data;
  } catch (error) {
    console.error('Verify password reset code error:', error);
    
    // Special handling for timeout or network errors
    if (!error.response) {
      throw new Error('Error de conexión. Por favor, intenta nuevamente en unos momentos.');
    }
    
    if (error.response?.data) {
      throw error;
    }
    throw new Error('Error al verificar el código de restablecimiento');
  }
};

export const resendPasswordResetCode = async (email) => {
  try {
    console.log('Resending password reset code for email:', email);
    const response = await apiClient.post('/auth/resend-password-reset/', { email }, {
      timeout: 60000 // 60 seconds timeout specifically for this request
    });
    return response.data;
  } catch (error) {
    console.error('Resend password reset code error:', error);
    
    // Special handling for timeout or network errors
    if (!error.response) {
      throw new Error('Error de conexión. Puede que el código se haya generado, intenta con el paso de verificación.');
    }
    
    if (error.response?.data) {
      throw error;
    }
    throw new Error('Error al reenviar el código de restablecimiento');
  }
};

export const getUsers = async (filters = {}) => {
  try {
    const token = localStorage.getItem('accessToken');
    
    // Construir parámetros de consulta
    const params = new URLSearchParams();
    if (filters.username) params.append('username', filters.username);
    if (filters.email) params.append('email', filters.email);
    if (filters.status) params.append('status', filters.status);
    if (filters.user_type) params.append('user_type', filters.user_type);
    if (filters.page) params.append('page', filters.page);
    if (filters.page_size) params.append('page_size', filters.page_size);
    
    const queryString = params.toString();
    const url = `/auth/users/${queryString ? `?${queryString}` : ''}`;
    
    console.log('Sending request to:', url); // Para debug
    console.log('Filters being sent:', filters); // Para debug
    
    const response = await apiClient.get(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error obteniendo usuarios:', error);
    throw error;
  }
}

export const getUserDetails = async (userId) => {
  try {
    const token = localStorage.getItem('accessToken');
    console.log(`Obteniendo detalles para usuario ID: ${userId}`);
    
    const response = await apiClient.get(`/auth/users/${userId}/`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    console.log('Respuesta getUserDetails:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo detalles del usuario:', error);
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url
    });
    
    // Si el usuario no existe, devolver error más específico
    if (error.response?.status === 404) {
      throw new Error(`Usuario con ID ${userId} no encontrado`);
    }
    
    // Si no hay permisos
    if (error.response?.status === 403) {
      throw new Error('No tienes permisos para ver este usuario');
    }
    
    throw error;
  }
}

export const deleteUser = async (userId) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/auth/users/${userId}/`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error eliminando usuario:', error);
    throw error;
  }
}

export const toggleUserStatus = async (userId, isActive) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.patch(`/auth/users/${userId}/`, {
      is_active: isActive
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error cambiando estado del usuario:', error);
    throw error;
  }
}

export const toggleUserBlock = async (userId, isBlocked, reason = null) => {
  try {
    const token = localStorage.getItem('accessToken');
    const data = {
      is_blocked: isBlocked
    };
    
    if (isBlocked && reason) {
      data.blocked_reason = reason;
    }
    
    const response = await apiClient.patch(`/auth/users/${userId}/`, data, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error cambiando bloqueo del usuario:', error);
    throw error;
  }
}

export const createUserAsAdmin = async (userData) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.post('/auth/admin/create-user/', userData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    return response.data;
  } catch (error) {
    // Preservar la respuesta completa del error para casos especiales
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Si es un error especial (cuenta existente, etc.), preservar la estructura
      if (errorData.error && typeof errorData.error === 'string') {
        const customError = new Error(errorData.message || 'Error al crear el usuario');
        customError.response = error.response;
        throw customError;
      }
      
      // Si es un array de errores de validación
      if (errorData.error && Array.isArray(errorData.error)) {
        const customError = new Error('Errores de validación');
        customError.errors = errorData.error;
        throw customError;
      }
    }
    throw error;
  }
}

export const bulkDeleteUsers = async (userIds) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.post('/auth/admin/bulk-delete-users/', 
      { user_ids: userIds },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Error al eliminar los usuarios');
  }
}